import { Check, ChevronLeft, ChevronRight, Pencil, Search, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "@/features/auth";
import { updateProjectTitle } from "@/features/project";

interface ProjectListItem {
  id: string;
  title: string;
  userEmail: string;
  userId: string;
  updatedAt: number;
  messageCount: number;
  totalCost: number;
  costPerCredit: number;
}

interface ProjectsResponse {
  projects: ProjectListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [titleSearch, setTitleSearch] = useState("");
  const [titleInput, setTitleInput] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [userIdInput, setUserIdInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (titleSearch) {
        params.set("title", titleSearch);
      }
      if (emailFilter) {
        params.set("email", emailFilter);
      }
      if (userIdFilter) {
        params.set("userId", userIdFilter);
      }
      const response = await fetch(`/api/admin/projects?${params}`, {
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 403) {
          setError("Access denied. Admin privileges required.");
          return;
        }
        throw new Error("Failed to fetch projects");
      }
      const data: ProjectsResponse = await response.json();
      setProjects(data.projects);
      setTotal(data.total);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, titleSearch, emailFilter, userIdFilter]);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate("/");
      return;
    }
    fetchProjects();
  }, [user, navigate, fetchProjects]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setTitleSearch(titleInput);
    setEmailFilter(emailInput);
    setUserIdFilter(userIdInput);
  };

  const startEditing = (project: ProjectListItem) => {
    setEditingId(project.id);
    setEditValue(project.title || "");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveTitle = async (projectId: string) => {
    const result = await updateProjectTitle(projectId, editValue);
    if (result) {
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId ? { ...p, title: result.title, updatedAt: result.updatedAt } : p,
        ),
      );
    }
    setEditingId(null);
    setEditValue("");
  };

  const totalPages = Math.ceil(total / pageSize);

  if (!user?.isAdmin) {
    return null;
  }

  return (
    <div className="min-h-dvh bg-slate-950 text-slate-200">
      <div
        className="mx-auto"
        style={{
          maxWidth: "min(1152px, 95vw)",
          padding: "min(5vw, 24px)",
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold">Admin - Project Management</h1>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            Back to Studio
          </button>
        </div>

        <form onSubmit={handleSearch} className="mb-6">
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
            }}
          >
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                type="text"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                placeholder="Search by project title..."
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg md:text-sm focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div className="relative">
              <input
                type="text"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Filter by email..."
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg md:text-sm focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div className="relative">
              <input
                type="text"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                placeholder="Filter by user ID..."
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg md:text-sm focus:outline-none focus:border-indigo-500"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors"
            >
              Search
            </button>
          </div>
        </form>

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-lg text-red-300">
            {error}
          </div>
        )}

        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
          <table style={{ minWidth: 700 }} className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Project
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase whitespace-nowrap">
                  Messages
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Cost
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase whitespace-nowrap">
                  $/100Credit
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Loading...
                  </td>
                </tr>
              ) : projects.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    No projects found
                  </td>
                </tr>
              ) : (
                projects.map((project) => (
                  <tr key={project.id} className="hover:bg-slate-800/50">
                    <td className="px-4 py-3">
                      {editingId === project.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveTitle(project.id);
                              if (e.key === "Escape") cancelEditing();
                            }}
                            className="flex-1 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-sm focus:outline-none focus:border-indigo-500"
                            style={{ minWidth: 120 }}
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => saveTitle(project.id)}
                            className="p-1 text-emerald-400 hover:text-emerald-300"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditing}
                            className="p-1 text-slate-400 hover:text-slate-300"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <Link
                            to={`/${project.id}`}
                            className="text-sm font-medium text-indigo-400 hover:text-indigo-300 hover:underline"
                          >
                            {project.title || "Untitled"}
                          </Link>
                          <button
                            type="button"
                            onClick={() => startEditing(project)}
                            className="p-1 text-slate-500 hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Pencil size={12} />
                          </button>
                        </div>
                      )}
                      <div className="text-xs text-slate-500 font-mono truncate max-w-[200px]">
                        {project.id}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm truncate max-w-[180px]">
                        {project.userEmail || "Unknown"}
                      </div>
                      <div className="text-xs text-slate-500 font-mono truncate max-w-[180px]">
                        {project.userId}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{project.messageCount}</td>
                    <td className="px-4 py-3 text-sm text-emerald-500 font-mono whitespace-nowrap">
                      ${project.totalCost.toFixed(4)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400 font-mono whitespace-nowrap">
                      ${(project.costPerCredit * 100).toFixed(4)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400 whitespace-nowrap">
                      {new Date(project.updatedAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
          <div className="text-sm text-slate-500">
            Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 bg-slate-800 hover:bg-slate-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm whitespace-nowrap">
              Page {page} of {totalPages || 1}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2 bg-slate-800 hover:bg-slate-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
